/**
 * Public (no-login) product inquiry page (route: /inquiry/:groupKey).
 * Flow: capture the visitor's location (GPS, or pincode/city fallback) → show the nearest
 * active retailers + the sales persons covering them (list + map) → submit an inquiry with a
 * contact (email and/or WhatsApp). Tailwind-styled to match the marketing site; the map uses
 * react-leaflet + OpenStreetMap (no API key). All calls hit the @Public inquiry API.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../lib/mapTiles';
import L from 'leaflet';
import {
  ArrowLeft, MapPin, LocateFixed, Loader2, Check, Phone, MessageCircle, Store, UserRound, CheckCircle2, Send,
} from 'lucide-react';
import { request } from '../api/client';
import { useAuthStore } from '../store/auth';
import { cn, message } from '../ui';
import { CONTACT } from './shared';
import { INDIAN_STATES } from '../constants/indianStates';
import { productGroupKey, groupBaseName, type PublicProduct } from './grouping';
import { toImageUrls } from '../lib/imageUrls';
import { Seo } from '../lib/seo';
import {
  getNearest, getNearestByPincode, getLocations, getByLocation, submitInquiry, fmtDistance,
  type NearestResult, type InquiryRetailer,
} from './inquiryApi';

const STATE_NAME = new Map(INDIAN_STATES.map((s) => [s.code, s.name]));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Self-contained SVG pins (Leaflet's default marker images don't resolve under bundlers).
const pin = (color: string) => L.divIcon({
  className: '', iconSize: [28, 38], iconAnchor: [14, 38], popupAnchor: [0, -34],
  html: `<svg viewBox="0 0 24 32" width="28" height="38" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="4.5" fill="white"/></svg>`,
});
const ICON_YOU = pin('#1677FF');
const ICON_RETAILER = pin('#339022');
const ICON_SP = pin('#E7B22C');

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

function DealerMap({ origin, retailers, selectedId, onSelect }: {
  origin: { lat: number; lng: number } | null;
  retailers: InquiryRetailer[];
  selectedId?: string;
  onSelect: (r: InquiryRetailer) => void;
}) {
  const retPts = retailers.filter((r) => r.latitude != null && r.longitude != null);
  const points: [number, number][] = [
    ...(origin ? [[origin.lat, origin.lng] as [number, number]] : []),
    ...retPts.map((r) => [r.latitude as number, r.longitude as number] as [number, number]),
    ...retailers.filter((r) => r.salesPerson?.latitude != null && r.salesPerson?.longitude != null).map((r) => [r.salesPerson!.latitude as number, r.salesPerson!.longitude as number] as [number, number]),
  ];
  const center: [number, number] = origin ? [origin.lat, origin.lng] : points[0] ?? [22.5, 80];
  return (
    <div className="overflow-hidden rounded-card border border-line" style={{ height: 380 }}>
      <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
        <FitBounds points={points} />
        {origin && (
          <Marker position={[origin.lat, origin.lng]} icon={ICON_YOU}><Popup>You are here</Popup></Marker>
        )}
        {retPts.map((r) => (
          <Marker key={`r-${r.id}`} position={[r.latitude as number, r.longitude as number]} icon={ICON_RETAILER}
            eventHandlers={{ click: () => onSelect(r) }}>
            <Popup>
              <b>{r.name}</b>{r.distanceMeters != null ? ` · ${fmtDistance(r.distanceMeters)}` : ''}<br />
              {r.address ?? ''}{r.mobile ? <><br />📞 {r.mobile}</> : null}
              {r.salesPerson ? <><br />SP: {r.salesPerson.name}{r.salesPerson.mobile ? ` · ${r.salesPerson.mobile}` : ''}</> : null}
            </Popup>
          </Marker>
        ))}
        {retailers.filter((r) => r.salesPerson?.latitude != null && r.salesPerson?.longitude != null).map((r) => (
          <Marker key={`sp-${r.salesPerson!.id}`} position={[r.salesPerson!.latitude as number, r.salesPerson!.longitude as number]} icon={ICON_SP}>
            <Popup><b>{r.salesPerson!.name}</b> (Sales person){r.salesPerson!.mobile ? <><br />📞 {r.salesPerson!.mobile}</> : null}</Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="flex flex-wrap gap-3 border-t border-line bg-surface px-3 py-2 text-[12px] text-muted">
        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-full bg-[#1677FF]" /> You</span>
        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-full bg-[#339022]" /> Retailer</span>
        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-full bg-[#E7B22C]" /> Sales person</span>
      </div>
    </div>
  );
}

/**
 * The site header is transparent with white links until the page scrolls, and is designed to sit on
 * the dark band every public page opens with. This page had none, so at the top the links were white
 * on the light page (measured contrast 1.09) and could not be read. A strip the header's height gives
 * it the dark ground it expects without restyling the form below.
 */
const NavBand = () => <div aria-hidden className="h-[var(--nav-h)] bg-night" />;

export function InquiryPage() {
  const { groupKey } = useParams();
  const key = decodeURIComponent(groupKey ?? '');
  const navigate = useNavigate();

  // Resolve the product group from the public catalogue.
  const { data, isLoading } = useQuery({
    queryKey: ['public-products', 'catalog'],
    queryFn: () => request<{ data: PublicProduct[] }>({ url: '/public/products', params: { pageSize: 500 } }),
  });
  const variants = useMemo(() => (data?.data ?? []).filter((p) => p.isActive !== false && productGroupKey(p) === key), [data, key]);
  const product = variants[0];
  const productName = variants.length ? groupBaseName(variants) : '';
  const productImg = toImageUrls(product?.imageUrls)[0] ?? product?.imageUrl ?? null;

  // Location + nearest state.
  const [mode, setMode] = useState<'gps' | 'manual'>('gps');
  const [coords, setCoords] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);
  const [locState, setLocState] = useState<'idle' | 'locating' | 'denied' | 'ok'>('idle');
  const [pincode, setPincode] = useState('');
  const [city, setCity] = useState('');
  const [selState, setSelState] = useState<string | undefined>();
  const [selCity, setSelCity] = useState<string | undefined>();
  const [nearest, setNearest] = useState<NearestResult | null>(null);
  const [loadingNear, setLoadingNear] = useState(false);
  const [chosen, setChosen] = useState<InquiryRetailer | null>(null);

  /*
   * Manual-mode dropdown data — only states/cities that actually have active dealers, and when the
   * visitor has typed a pincode, only THEIR state.
   *
   * Browsing manually was the hole: the pincode box and the state dropdown were independent, so a
   * visitor could search an Odisha pincode, find nothing, switch to manual, and pick a Maharashtra
   * dealer — which is how a real lead ended up in a sales queue 1,300km from the person who raised
   * it. Fencing the list to their own state makes that pair impossible to build.
   */
  const { data: locations } = useQuery({
    queryKey: ['inquiry-locations', pincode.trim()],
    queryFn: () => getLocations(pincode.trim() || undefined),
    enabled: mode === 'manual',
  });
  /** The state the visitor's pincode resolved to, when it resolved at all. */
  const pinState = locations?.stateCode ?? null;
  const stateOptions = useMemo(
    () => (locations?.states ?? []).map((code) => ({ code, name: STATE_NAME.get(code) ?? code })).sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );
  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of locations?.cities ?? []) if (!selState || c.stateCode === selState) set.add(c.city);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [locations, selState]);

  const switchMode = (m: 'gps' | 'manual') => {
    setMode(m); setNearest(null); setChosen(null);
    if (m === 'gps') { setSelState(undefined); setSelCity(undefined); }
    else { setCoords(null); setLocState('idle'); }
  };

  /* One state on offer means the pincode already decided it — select it rather than making the
     visitor choose from a list of one. Only ever auto-selects while nothing is chosen, so it cannot
     fight someone who is deliberately switching state. */
  useEffect(() => {
    if (mode === 'manual' && !selState && stateOptions.length === 1) setSelState(stateOptions[0].code);
  }, [mode, selState, stateOptions]);

  // Manual browse: (re)load retailers whenever the chosen state/city changes.
  useEffect(() => {
    if (mode !== 'manual' || !selState) return;
    let cancelled = false;
    setLoadingNear(true); setCoords(null); setChosen(null);
    getByLocation(selState, selCity)
      .then((r) => { if (!cancelled) setNearest(r); })
      .catch(() => { if (!cancelled) message.error('Could not load dealers for that area.'); })
      .finally(() => { if (!cancelled) setLoadingNear(false); });
    return () => { cancelled = true; };
  }, [mode, selState, selCity, message]);

  // Contact form.
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ inquiryNo: string; publicToken: string } | null>(null);
  const [honeypot, setHoneypot] = useState(''); // bots fill this hidden field

  /*
   * A SIGNED-IN customer is not a stranger: their name, number and email are on their account, so
   * the form starts filled in rather than asking them to type what we already hold. Only fills a
   * blank field — whatever they have typed stays theirs. The API also recognises them from their
   * login, links the enquiry to their account and acknowledges it in their own notifications.
   */
  const signedIn = useAuthStore((s) => s.user);
  const signedInCustomer = !!signedIn && (signedIn.role === 'FARMER' || signedIn.role === 'RETAILER');
  const { data: me } = useQuery({
    queryKey: ['profile-me'],
    queryFn: () => request<any>({ url: '/profile/me' }),
    enabled: signedInCustomer,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (!me) return;
    setName((v) => v || me.fullName || '');
    setPhone((v) => v || me.mobile || '');
    setEmail((v) => v || me.email || '');
  }, [me]);



  const formRef = useRef<HTMLDivElement>(null);

  const loadNearestByCoords = async (lat: number, lng: number) => {
    setLoadingNear(true);
    try { setNearest(await getNearest(lat, lng, 25)); }
    catch { message.error('Could not load nearby dealers — please try again.'); }
    finally { setLoadingNear(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setLocState('denied'); return; }
    setLocState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setCoords(c); setLocState('ok'); void loadNearestByCoords(c.lat, c.lng);
      },
      () => { setLocState('denied'); },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  const findByPincode = async () => {
    if (!pincode.trim() && !city.trim()) { message.warning('Enter a pincode or city'); return; }
    setLoadingNear(true); setCoords(null);
    try { setNearest(await getNearestByPincode(pincode.trim() || undefined, city.trim() || undefined)); }
    catch { message.error('Could not find dealers for that location.'); }
    finally { setLoadingNear(false); }
  };

  // After results land, scroll the form into view for momentum.
  useEffect(() => { if (nearest) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [nearest]);

  /*
   * A dealer must be picked — unless the visitor's area genuinely has none, in which case the lead
   * goes to central triage and we route it by hand.
   *
   * The second clause is new and load-bearing. Now that the state list is fenced to the visitor's
   * own state, a pincode in a state we do not cover leaves NO state to select — and without it the
   * visitor could never reach a dealer list, never satisfy "pick a dealer", and never submit. That
   * would turn "we have no dealer near you" into a broken form, when it is exactly the case central
   * triage exists for.
   */
  const noDealersInTheirState = mode === 'manual' && !!locations && stateOptions.length === 0;
  const dealerRequired = !(nearest && nearest.retailers.length === 0) && !noDealersInTheirState;
  const canSubmit = !!name.trim() && EMAIL_RE.test(email.trim()) && !!phone.trim()
    && (!!chosen || !dealerRequired) && !submitting;

  const onSubmit = async () => {
    if (!name.trim()) { message.warning('Please enter your name'); return; }
    if (!EMAIL_RE.test(email.trim())) { message.warning('Please enter a valid email address'); return; }
    if (!phone.trim()) { message.warning('Please add a WhatsApp / phone number'); return; }
    if (dealerRequired && !chosen) { message.warning('Please select a dealer'); return; }
    setSubmitting(true);
    try {
      const res = await submitInquiry({
        customerName: name.trim(),
        customerEmail: email.trim() || undefined,
        customerWhatsapp: phone.trim() || undefined,
        customerPhone: phone.trim() || undefined,
        productId: product ? String(product.id) : undefined,
        productNameText: product ? undefined : productName || undefined,
        message: note.trim() || undefined,
        /*
         * SEND THE LOCATION THE DEALER WAS ACTUALLY CHOSEN IN — nothing else.
         *
         * The pincode box was previously sent whenever there was no GPS, whatever mode the dealer
         * was picked in. So a visitor who searched a pincode, found nothing, then browsed manually
         * to another state submitted one person's pincode beside another state's dealer, and the
         * lead recorded a contradiction nobody could resolve afterwards. Each branch below sends one
         * coherent location, and the API refuses the pair if they ever disagree again.
         */
        ...(coords
          ? {
            latitude: coords.lat,
            longitude: coords.lng,
            accuracyMeters: coords.accuracy != null ? Math.round(coords.accuracy) : undefined,
          }
          : mode === 'manual'
            ? {
              // The area they picked IS their declared area. The typed pincode rides along only when
              // it belongs to that same state — otherwise it is a leftover from an abandoned search.
              stateCode: selState || undefined,
              city: selCity || city.trim() || undefined,
              pincode: pinState && pinState === selState && pincode.trim() ? pincode.trim() : undefined,
            }
            : {
              pincode: pincode.trim() || undefined,
              city: city.trim() || undefined,
              stateCode: nearest?.stateCode || undefined,
            }),
        chosenRetailerId: chosen ? chosen.id : undefined,
        // Who they are is known when they are signed in — it decides whether a farmer record is made.
        enquirerType: signedInCustomer ? signedIn!.role : undefined,
        website: honeypot.trim() || undefined, // honeypot — empty for real users
      });
      setResult({ inquiryNo: res.inquiryNo, publicToken: res.publicToken });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? 'Could not send your inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoading && !variants.length) {
    return (
      <>
      <NavBand />
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <p className="text-lg font-bold text-brand-800! dark:text-brand-300!">Product not found</p>
        <Link to="/catalog" className="mt-3 inline-block text-sm font-bold text-brand-700! hover:underline">Browse all products</Link>
      </div>
      </>
    );
  }

  // ── Success screen ──
  if (result) {
    return (
      <>
      <NavBand />
      <div className="mx-auto max-w-2xl px-4 py-14 sm:px-6">
        <div className="rounded-card border border-line bg-surface p-8 text-center">
          <span className="mx-auto grid size-16 place-items-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
            <CheckCircle2 className="size-9" />
          </span>
          <h1 className="mt-5 text-2xl font-extrabold text-brand-800! dark:text-brand-300!">Inquiry sent!</h1>
          <p className="mx-auto mt-2 max-w-md text-[15px] text-muted">
            Thanks, {name.trim()}. Your inquiry <b className="text-content">{result.inquiryNo}</b> for <b className="text-content">{productName}</b> has
            been received{chosen ? <> and routed to <b className="text-content">{chosen.name}</b></> : ''}. Our team will reach out to you soon.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={`/inquiry/status/${result.publicToken}`}
              className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-brand-600! px-5 text-sm font-bold text-white! shadow-soft hover:bg-brand-700!">
              Track this inquiry
            </Link>
            <Link to="/catalog" className="inline-flex h-11 items-center rounded-[10px] border border-line px-5 text-sm font-semibold text-content hover:bg-brand-50 dark:hover:bg-brand-900/20">
              Browse more products
            </Link>
          </div>
          <p className="mt-5 text-xs text-muted">Tip: save your inquiry number, or bookmark the tracking link.</p>
        </div>
      </div>
      </>
    );
  }

  const dealerCards = nearest?.retailers?.length ? (
    <div className="min-w-0 space-y-3 lg:max-h-[440px] lg:overflow-y-auto lg:pr-1">
      {nearest.retailers.map((r) => {
        const sel = chosen?.id === r.id;
        return (
          <button key={r.id} type="button" onClick={() => setChosen(r)}
            className={cn('w-full rounded-[12px] border p-4 text-left transition-all',
              sel ? 'border-brand-600 bg-brand-50/60 ring-2 ring-brand-500/30 dark:bg-brand-900/20' : 'border-line bg-surface hover:border-brand-300')}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-content">{r.name}</div>
              {r.distanceMeters != null && <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[12px] font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">{fmtDistance(r.distanceMeters)}</span>}
            </div>
            {r.address && <div className="mt-0.5 text-[13px] text-muted">{r.address}</div>}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
              {r.mobile && <a href={`tel:${r.mobile}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-brand-700! hover:underline dark:text-brand-300!"><Phone className="size-3.5" /> {r.mobile}</a>}
              {r.whatsapp && <a href={`https://wa.me/${r.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-brand-700! hover:underline dark:text-brand-300!"><MessageCircle className="size-3.5" /> WhatsApp</a>}
            </div>
            {r.salesPerson && (
              <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-[8px] bg-surface-2/70 px-2.5 py-1.5 text-[13px] text-content">
                <UserRound className="size-3.5 shrink-0 text-harvest-500" />
                <span className="font-medium break-words">{r.salesPerson.name}</span>
                {r.salesPerson.mobile && <a href={`tel:${r.salesPerson.mobile}`} onClick={(e) => e.stopPropagation()} className="text-brand-700! hover:underline dark:text-brand-300!">· {r.salesPerson.mobile}</a>}
              </div>
            )}
            <div className={cn('mt-2 inline-flex items-center gap-1 text-[12px] font-bold', sel ? 'text-brand-700 dark:text-brand-300' : 'text-muted')}>
              {sel ? <><Check className="size-3.5" /> Selected</> : 'Tap to select this dealer'}
            </div>
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <>
    <NavBand />
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Seo
        noindex
        title={productName ? `Request a quote — ${productName}` : 'Request a quote'}
        description="Request a quote and find your nearest Green Bay Biotech dealer."
      />
      <Link to={`/catalog/${encodeURIComponent(key)}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted! hover:text-brand-700!">
        <ArrowLeft className="size-4" /> Back to product
      </Link>

      {/* Header (full width) */}
      <div className="mt-5 flex items-center gap-4 rounded-card border border-line bg-surface p-4">
        <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-[12px] bg-gradient-to-br from-brand-50 to-surface-2 dark:from-brand-900/20">
          {productImg ? <img src={productImg} alt={productName} className="max-h-[85%] max-w-[85%] object-contain mix-blend-multiply dark:mix-blend-normal" /> : <span className="text-3xl">🌱</span>}
        </div>
        <div>
          <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-600">Product inquiry</div>
          <h1 className="text-2xl font-extrabold text-brand-800! dark:text-brand-300!">{productName || '…'}</h1>
          <p className="text-sm text-muted">Find your nearest dealer & sales person, then send your inquiry.</p>
        </div>
      </div>

      {/* Row 1 — find dealer (left) + results (right) */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* LEFT — Find your dealer (sticky on desktop) */}
        <section className="rounded-card border border-line bg-surface p-5 lg:col-span-5 lg:sticky lg:top-24 lg:self-start xl:col-span-4">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-content"><MapPin className="size-4 text-brand-600" /> 1. Find your dealer</h2>
          <div className="mt-3 inline-flex rounded-[10px] border border-line p-1">
            {(['gps', 'manual'] as const).map((m) => (
              <button key={m} type="button" onClick={() => switchMode(m)}
                className={cn('rounded-[8px] px-4 py-2 text-sm font-semibold transition-colors',
                  mode === m ? 'bg-brand-600! text-white!' : 'text-content hover:bg-brand-50 dark:hover:bg-brand-900/20')}>
                {m === 'gps' ? 'Use my location' : 'Choose manually'}
              </button>
            ))}
          </div>

          {mode === 'gps' ? (
            <>
              <p className="mt-3 text-sm text-muted">Share your location to see the nearest retailers & sales persons.</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button type="button" onClick={useMyLocation} disabled={locState === 'locating'}
                  className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-brand-600! px-5 text-sm font-bold text-white! shadow-soft hover:bg-brand-700! disabled:opacity-60">
                  {locState === 'locating' ? <Loader2 className="size-4 animate-spin" /> : <LocateFixed className="size-4" />}
                  {locState === 'locating' ? 'Locating…' : 'Use my current location'}
                </button>
                {coords && <span className="inline-flex items-center gap-1.5 text-sm text-brand-700 dark:text-brand-300"><Check className="size-4" /> Location captured{coords.accuracy ? ` (±${Math.round(coords.accuracy)} m)` : ''}</span>}
              </div>
              {(locState === 'denied' || (!coords && nearest?.byPincode)) && (
                <div className="mt-4 rounded-[12px] border border-dashed border-line bg-surface-2/60 p-4">
                  <p className="text-sm text-muted">No location access? Enter your <b className="text-content">pincode</b> or <b className="text-content">city</b> — or switch to “Choose manually”.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Pincode"
                      className="h-11 w-32 rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500" />
                    <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City"
                      className="h-11 flex-1 rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500" />
                    <button type="button" onClick={findByPincode}
                      className="inline-flex h-11 items-center rounded-[10px] border border-brand-600 px-5 text-sm font-bold text-brand-700! hover:bg-brand-50 dark:text-brand-300!">
                      Find
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted">Pick your area to see the dealers there. Only locations we currently serve are listed.</p>
              <div className="mt-3 space-y-3">
                {/* Say WHY only one state is on offer. A dropdown that silently holds a single option
                    reads as broken; told plainly, it reads as the form knowing where they are — and
                    it gives them the way to widen it back (clear the pincode). */}
                {pinState && stateOptions.length > 0 && (
                  <p className="rounded-[10px] border border-brand-200 bg-brand-50/60 px-3 py-2 text-[13px] text-content dark:border-brand-800 dark:bg-brand-900/20">
                    Showing dealers in <strong>{STATE_NAME.get(pinState) ?? pinState}</strong>, for pincode {pincode.trim()}.
                    {' '}Clear the pincode above to browse another state.
                  </p>
                )}
                {/* No dealer in their state. Said plainly, with the reassurance that the enquiry still
                    goes somewhere — the submit button is already unblocked for exactly this case. */}
                {noDealersInTheirState && (
                  <p className="rounded-[10px] border border-harvest-300 bg-harvest-50/70 px-3 py-2 text-[13px] text-content dark:border-harvest-700 dark:bg-harvest-900/20">
                    We don&rsquo;t have a dealer in <strong>{STATE_NAME.get(pinState ?? '') ?? 'your area'}</strong> yet.
                    {' '}Send your enquiry anyway — our team will pick it up directly and get back to you.
                  </p>
                )}
                <label className="block">
                  <span className="mb-1 block text-[13px] font-semibold text-content">State</span>
                  <select value={selState ?? ''} onChange={(e) => { setSelState(e.target.value || undefined); setSelCity(undefined); }}
                    className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500">
                    <option value="">Select state…</option>
                    {stateOptions.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[13px] font-semibold text-content">City <span className="font-normal text-muted">(optional)</span></span>
                  <select value={selCity ?? ''} disabled={!selState} onChange={(e) => setSelCity(e.target.value || undefined)}
                    className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500 disabled:opacity-50">
                    <option value="">{selState ? 'All cities in state' : 'Select a state first'}</option>
                    {cityOptions.map((cty) => <option key={cty} value={cty}>{cty}</option>)}
                  </select>
                </label>
              </div>
            </>
          )}

          {chosen && (
            <div className="mt-4 rounded-[10px] border border-brand-200 bg-brand-50/60 p-3 text-[13px] dark:border-brand-800 dark:bg-brand-900/20">
              <span className="font-semibold text-brand-800 dark:text-brand-300">Selected dealer:</span> {chosen.name}
              {chosen.salesPerson ? <> · {chosen.salesPerson.name}</> : ''}
            </div>
          )}
        </section>

        {/* RIGHT — dealer results (map + cards) */}
        <section className="rounded-card border border-line bg-surface p-5 lg:col-span-7 xl:col-span-8">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-content"><Store className="size-4 text-brand-600" /> {mode === 'manual' ? 'Dealers in this area' : 'Nearby dealers & sales persons'}</h2>

          {mode === 'manual' && !!nearest?.retailers?.length && (
            <label className="mt-3 block max-w-md">
              <span className="mb-1 block text-[13px] font-semibold text-content">Select a retailer <span className="text-red-500">*</span></span>
              <select value={chosen?.id ?? ''} onChange={(e) => setChosen(nearest.retailers.find((r) => r.id === e.target.value) ?? null)}
                className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500">
                <option value="">— choose a retailer —</option>
                {nearest.retailers.map((r) => <option key={r.id} value={r.id}>{r.name}{r.city ? ` · ${r.city}` : ''}</option>)}
              </select>
            </label>
          )}

          {loadingNear ? (
            <div className="grid min-h-[300px] place-items-center text-muted"><Loader2 className="size-7 animate-spin" /></div>
          ) : !nearest ? (
            <div className="mt-4 grid min-h-[300px] place-items-center rounded-[12px] border border-dashed border-line p-8 text-center">
              <div>
                <span className="mx-auto grid size-12 place-items-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300"><MapPin className="size-6" /></span>
                <p className="mt-3 text-sm font-semibold text-content">See dealers near you</p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted">Use <b className="text-content">Use my location</b> or pick your area under <b className="text-content">Find your dealer</b>, and matching dealers & sales persons will appear here on the map.</p>
              </div>
            </div>
          ) : !nearest.retailers.length ? (
            <div className="mt-4 grid min-h-[200px] place-items-center rounded-[12px] border border-dashed border-line p-6 text-center text-sm text-muted">
              No dealers found in this area. You can still send your inquiry below — our team will route it to the right person.
            </div>
          ) : (
            <div className="mt-4 grid items-start gap-5 xl:grid-cols-2">
              <div className="min-w-0"><DealerMap origin={coords} retailers={nearest.retailers} selectedId={chosen?.id} onSelect={setChosen} /></div>
              {dealerCards}
            </div>
          )}
        </section>
      </div>

      {/* Row 2 — your details (full width) */}
      <section ref={formRef} className="mt-6 rounded-card border border-line bg-surface p-5">
        <h2 className="flex items-center gap-2 text-[15px] font-bold text-content"><Send className="size-4 text-brand-600" /> 2. Your details</h2>
        <p className="mt-1 text-sm text-muted">All fields are required (except the message) so the dealer / sales person can reach you.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-content">Your name <span className="text-red-500">*</span></span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" maxLength={120}
              className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-content">WhatsApp / phone <span className="text-red-500">*</span></span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" maxLength={20}
              className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[13px] font-semibold text-content">Email <span className="text-red-500">*</span></span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" maxLength={160} type="email"
              className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-content outline-none focus:border-brand-500" />
          </label>
          <label className="block sm:col-span-2 lg:col-span-3">
            <span className="mb-1 block text-[13px] font-semibold text-content">Message <span className="text-muted font-normal">(optional)</span></span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder="Quantity needed, questions, preferred call time…"
              className="w-full rounded-[10px] border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
          </label>
        </div>
        {/* Honeypot — hidden from users, bots fill it. */}
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden className="hidden"
          name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button type="button" onClick={onSubmit} disabled={!canSubmit}
            className="inline-flex h-12 items-center gap-2 rounded-[10px] bg-brand-600! px-7 text-[15px] font-bold text-white! shadow-soft transition-all hover:-translate-y-0.5 hover:bg-brand-700! disabled:translate-y-0 disabled:opacity-50">
            {submitting ? <Loader2 className="size-5 animate-spin" /> : <Send className="size-5" />} Send inquiry
          </button>
          <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 text-sm text-muted! hover:text-brand-700!">
            <Phone className="size-4 text-brand-600" /> Or call us: <strong className="text-content">{CONTACT.phone}</strong>
          </a>
          {dealerRequired && !chosen && <span className="text-[13px] text-muted">Select a dealer above to continue.</span>}
        </div>
      </section>
    </div>
    </>
  );
}
